import { useCallback, useMemo } from 'react';

import { Finding } from '@/api';
import { useKeyImageLabel } from '@/shared/hook';
import { mergeDefaultAndCommonFindings } from '@/shared/util';
import { useDefaultFindingStore, useFindingStore } from '@/store';

import { KEY_IMAGES_LINK, NECK_ORGAN, NECK_SUB_ORGAN } from './nav-menu.constant';

function getKeyImageCount(findings: Finding[]): number {
  return findings.reduce((count, findingData) => {
    const keyImages = findingData.renderedSummary.keyImages.filter(
      (ki) => ki.status !== 'deprecated',
    );

    return count + keyImages.length;
  }, 0);
}

export function useKiLabelCount(): (link: string) => number {
  const { getActiveKeyImageLabels } = useKeyImageLabel();
  const { findings } = useFindingStore();
  const { defaultFindings } = useDefaultFindingStore();

  const mergedFindings = useMemo(
    () => mergeDefaultAndCommonFindings(defaultFindings, findings),
    [defaultFindings, findings],
  );

  const neckFindings = useMemo(
    () => NECK_SUB_ORGAN.flatMap((organ) => mergedFindings[organ] || []),
    [mergedFindings],
  );

  return useCallback(
    (link: string) => {
      if (link === NECK_ORGAN) {
        return getKeyImageCount(neckFindings);
      }

      if (link === KEY_IMAGES_LINK) {
        return getActiveKeyImageLabels().length;
      }

      return getKeyImageCount(mergedFindings[link] || []);
    },
    [neckFindings, mergedFindings, getActiveKeyImageLabels],
  );
}
